import React, { useState, useMemo } from "react";
import { Spin } from "antd";
import { useSelector } from "react-redux";
import AddNewCountry from "../../modals/AddNewCountry";
import DeleteCountry from "../../modals/DeleteCountry";
import UpdateCountry from "../../modals/UpdateCountry";
import { useTranslation } from "react-i18next";
import { UTILS } from "utils";
import useUserType from "hooks/useUserType";
import RenderTime from "features/shipping_methods/common/RenderTime";
import AppHeading1 from "components/ui/AppHeading1";
import AppSearchInput from "components/ui/AppSearchInput";
import AppPaginatedTable from "components/ui/AppPaginatedTable";
import AppButton from "components/ui/AppButton";
import AppIcon from "components/ui/AppIcon";
function CountriesTable() {
  const userType = useUserType() || UTILS.getCurrentUser()?.userType;
  const [searchTerm, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dialogContentId, setDialogContentId] = useState(-1);
  const [selectedDataRows, setSelectedDataRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const { t } = useTranslation();
  const { selectedStore, addedCountries } = useSelector(
    (state) => state.AreasReducer
  );
  const { isLoading } = useSelector((state) => state.LoaderReducer);

  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const columns = [
    {
      key: "countryID",
      title: t("country name"),
      sorter: (a, b) =>
        a?.countryID?.countryNameEN?.localeCompare(b?.countryID?.countryNameEN),
      dataIndex: "cityName",
      render: (val, row, index) => {
        return currentLanguage === "EN"
          ? row?.countryID?.countryNameEN
          : row?.countryID?.countryNameAR;
      },
    },
    {
      key: "minOrder",
      title: t("Min. Basket Value"),
      dataIndex: "minOrder",
      sorter: {
        compare: (a, b) => a.minOrder - b.minOrder,
        multiple: 3,
      },
      render: (val) => {
        return new Intl.NumberFormat(currentLanguage, {
          style: "currency",
          currency: "AED",
        }).format(val);
      },
    },
    {
      key: "deliveryFee",
      title: t("Delivery Fee"),
      dataIndex: "deliveryFee",
      sorter: {
        compare: (a, b) => a.deliveryFee - b.deliveryFee,
        multiple: 3,
      },
      render: (val) => {
        return new Intl.NumberFormat(currentLanguage, {
          style: "currency",
          currency: "AED",
        }).format(val);
      },
    },

    {
      key: "deliveryTime",
      title: t("Delivery Time"),
      dataIndex: "deliveryTime",
      sorter: {
        compare: (a, b) => a.deliveryTime - b.deliveryTime,
        multiple: 3,
      },
      render: (val, row, index) => {
        return <RenderTime {...{ val, row, index }} />;
      },
    },
    {
      title: t("Actions"),
      render: (_, row) => (
        <div className="flex justify-start gap-3">
          <span onClick={() => editCountry(row)}>
            <AppIcon name="edit" className="cursor-pointer" />
          </span>
          <span onClick={() => deleteCountry(row)}>
            <AppIcon name="trash" className="cursor-pointer" />
          </span>
        </div>
      ),
    },
  ];

  const DG_CONTENT_ID = {
    ADD_COUNTRY: 0,
    DELETE_COUNTRY: 1,
    UPDATE_COUNTRY: 2,
    ADD_ITEM_TO_COUNTRY: 3,
  };

  const onClose = () => {
    setOpen(false);
    setSelectedDataRows([]);
    setSelectedKeys([]);
  };
  const renderModalContent = () => {
    switch (dialogContentId) {
      case 0:
        return <AddNewCountry open={open} onClose={onClose} />;
      case 1:
        return (
          <DeleteCountry
            open={open}
            selectedAreas={selectedDataRows}
            selectedRow={selectedDataRows}
            onClose={onClose}
          />
        );
      case 2:
        return (
          <UpdateCountry
            open={open}
            selectedCountries={selectedDataRows}
            onClose={onClose}
          />
        );
      default:
        break;
    }
  };

  const Items = useMemo(() => {
    const term = searchTerm?.trim()?.toLowerCase();
    const countries = addedCountries?.countryDeliveryData || [];
    if (!term) {
      return countries.map((item, index) => ({ ...item, key: index }));
    }
    return countries
      .filter((item) => {
        const countryEn = item?.countryID?.countryNameEN || "";
        const countryAr = item?.countryID?.countryNameAR || "";
        return (
          countryEn.toLowerCase().includes(term) ||
          countryAr.toLowerCase().includes(term)
        );
      })
      .map((item, index) => ({ ...item, key: index }));
  }, [addedCountries, searchTerm]);

  const handSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const deleteCountry = (row) => {
    setSelectedDataRows(row ? [row] : []);
    setOpen(true);
    setDialogContentId(DG_CONTENT_ID.DELETE_COUNTRY);
  };

  // Selection is held as ids against the unfiltered list, so narrowing the search
  // never silently drops a row the user already ticked.
  const selectedRows = (addedCountries?.countryDeliveryData || []).filter((row) =>
    selectedKeys.includes(row?._id)
  );

  const deleteSelected = () => {
    if (selectedRows.length === 0) return;
    setSelectedDataRows(selectedRows);
    setOpen(true);
    setDialogContentId(DG_CONTENT_ID.DELETE_COUNTRY);
  };
  const editCountry = (row) => {
    setSelectedDataRows(row ? [row] : []);
    setOpen(true);
    setDialogContentId(DG_CONTENT_ID.UPDATE_COUNTRY);
  };
  const AddCountry = () => {
    setOpen(true);
    setDialogContentId(DG_CONTENT_ID.ADD_COUNTRY);
  };

  return (
    <div>
      <Spin spinning={isLoading}>
        {selectedStore && (
          <>
            {renderModalContent()}
            <div className="my-3 flex flex-col md:flex-row flex-shrink-0 items-center justify-between">
              <AppHeading1>Countries for {selectedStore?.label}</AppHeading1>
              <div className="flex flex-col md:flex-row items-center gap-3">
                <AppSearchInput
                  value={searchTerm}
                  placeholder={t("search country by name")}
                  onInput={handSearch}
                />
                {userType !== "MANAGER" && (
                  <div className="md:mt-0 mt-3 flex gap-3">
                    <AppButton onClick={AddCountry}>
                      {t("Add Delivery Country")}
                    </AppButton>
                  </div>
                )}
                {/* Bulk delete follows the same rule as the row delete icon,
                    which is available to every role on every delivery table. */}
                {selectedKeys.length > 0 && (
                  <div className="md:mt-0 mt-3 flex gap-3">
                    <AppButton variant="danger" onClick={deleteSelected}>
                      {t("Delete Selected")} ({selectedKeys.length})
                    </AppButton>
                    <AppButton
                      variant="secondary"
                      onClick={() => setSelectedKeys([])}
                    >
                      {t("Clear selection")}
                    </AppButton>
                  </div>
                )}
              </div>
            </div>
            <div className="min-h-0 min-w-0 w-full max-w-full flex-1 bg-white">
              <AppPaginatedTable
                data={Items}
                total={Items?.length || 0}
                page={page}
                pageSize={pageSize}
                clientSidePagination
                onPageChange={(p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                }}
                columns={columns}
                selectable
                selectedRowKeys={selectedKeys}
                onSelectionChange={setSelectedKeys}
              />
            </div>
          </>
        )}
      </Spin>
    </div>
  );
}

export default CountriesTable;
